import {
    Arrow,
    Count,
    Cutout,
    Flag,
    FloorLevelBreaklineTool,
    Highlight,
    Line,
    Measure,
    Overlay,
    Pan,
    Pen,
    Pitch,
    RadiusLine,
    Rectangle,
    Select,
} from '../lib/toolBoxes/2D'
import { Border } from '../lib/toolBoxes/2D/tools/border/Border.tool'
import { isTemplatePage } from '../sagas/2D/reviewPlan'
import {
    getSelectedMaterial,
    selectedAiSuggestionIds,
    selectFocusedOverlayChunkId,
    selectIMUPDrawableLocations,
} from '../slices/2D'
import { selectJoistDirection } from '../slices/common'
import { selectDocumentMappingByActiveDocument } from '../slices/documents'
import { selectActiveGeometryGroup } from '../slices/geometry'
import {
    selectAreConflictsBeingReviewed,
    selectAreConflictsResolutionComplete,
    selectAreMaterialInteractionsBlocked,
    selectIsMaterialGroupActivationBlocked,
    selectIsOverlaidChunkLocked,
} from '../slices/masterSetPlan'
import { ToolbarButtonItem } from '../types'
import { useAppSelector } from '../../hooks'
import { Project } from '../../models/project'
import { DRAWABLE_TYPES, DRAWING_TYPES, NEW_IMUP_JOIST_TYPES_GROUP } from '../../shared/constants/drawable-types'
import { JOIST_DIRECTION } from '../../shared/constants/joist'
import { determineAllSupportedDrawingTypes } from '../../shared/services/drawable-type-service'

export const getCreationToolTypeMapping = () => {
    return {
        [Count.NAME]: DRAWING_TYPES.POINT,
        [Line.NAME]: DRAWING_TYPES.SECTION,
        [Rectangle.NAME]: DRAWING_TYPES.AREA,
        [Pen.NAME]: DRAWING_TYPES.AREA,
        [RadiusLine.NAME]: DRAWING_TYPES.RADIUS,
        [Border.NAME]: DRAWING_TYPES.SECTION,
    }
}

export const getEditingToolTypeMapping = () => {
    return {
        [Cutout.NAME]: DRAWING_TYPES.AREA,
        [Arrow.NAME]: DRAWING_TYPES.AREA,
    }
}

const enabledToolsWithoutPageScale = () => {
    return [Select.NAME, Pan.NAME, Highlight.NAME]
}

const enabledToolsWhenPageScaleIsSet = () => {
    return [Measure.NAME, Flag.NAME, Pitch.NAME]
}

const enabledToolsForAiSuggestion = () => {
    return [Select.NAME, Pan.NAME, Measure.NAME, Highlight.NAME]
}

const isCreationTool = (toolName: string) => {
    const creationToolTypeMapping = getCreationToolTypeMapping()

    return creationToolTypeMapping[toolName] !== undefined
}

const isEditingTool = (toolName: string) => {
    const editingToolMappingByShape = getEditingToolTypeMapping()

    return editingToolMappingByShape[toolName] !== undefined
}

export interface ToolEnablerParams {
    activeDrawableGroup: ReturnType<typeof selectActiveGeometryGroup>
    drawableLocations: ReturnType<typeof selectIMUPDrawableLocations>
    selectedMatType: ReturnType<typeof getSelectedMaterial>
    joistDirection: ReturnType<typeof selectJoistDirection>
    project: Project | null
    overlayChunkId: ReturnType<typeof selectFocusedOverlayChunkId>
    isOverlaidChunkLocked: ReturnType<typeof selectIsOverlaidChunkLocked>
    selectedAiSuggestions: ReturnType<typeof selectedAiSuggestionIds>
    activeDocumentMapping: ReturnType<typeof selectDocumentMappingByActiveDocument>
    areConflictsBeingReviewed: ReturnType<typeof selectAreConflictsBeingReviewed>
    isConflictResolutionComplete: ReturnType<typeof selectAreConflictsResolutionComplete>
    areMaterialInteractionsBlocked: ReturnType<typeof selectAreMaterialInteractionsBlocked>
    isMaterialGroupActivationBlocked: ReturnType<typeof selectIsMaterialGroupActivationBlocked>
}

export const isToolEnabled = (toolName: ToolbarButtonItem['toolName'], params: ToolEnablerParams): boolean => {
    const {
        activeDrawableGroup,
        drawableLocations,
        selectedMatType,
        joistDirection,
        project,
        overlayChunkId,
        isOverlaidChunkLocked,
        selectedAiSuggestions,
        activeDocumentMapping,
        areConflictsBeingReviewed,
        isConflictResolutionComplete,
        areMaterialInteractionsBlocked,
        isMaterialGroupActivationBlocked,
    } = params

    if (project?.is_master_set && isTemplatePage(activeDocumentMapping)) {
        return toolName === Highlight.NAME
    }

    const isActiveDrawableGroupWithOpenings = (activeDrawableGroup?.openings?.length ?? 0) > 0

    if (toolName === Overlay.NAME) {
        return true
    }

    if (
        areMaterialInteractionsBlocked &&
        (isEditingTool(toolName) ||
            isCreationTool(toolName) ||
            toolName === Select.NAME ||
            toolName === FloorLevelBreaklineTool.NAME)
    ) {
        return false
    }

    if (project?.is_master_set && overlayChunkId && !isOverlaidChunkLocked) {
        return !(
            isEditingTool(toolName) ||
            isCreationTool(toolName) ||
            Select.NAME === toolName ||
            Pan.NAME === toolName
        )
    }

    if (
        isMaterialGroupActivationBlocked &&
        (isCreationTool(toolName) || toolName === Arrow.NAME || toolName === FloorLevelBreaklineTool.NAME)
    ) {
        return false
    }

    // Conflict cutouts edit modification geometry without activating a material group.
    if (
        project?.is_master_set &&
        (areConflictsBeingReviewed || (overlayChunkId && isOverlaidChunkLocked && !isConflictResolutionComplete))
    ) {
        if (toolName === Cutout.NAME) {
            return true
        }
    }

    // tools that are enabled without page scale
    if (enabledToolsWithoutPageScale().includes(toolName)) {
        return true
    }

    // when the page scale is not set, disable all rest tools
    if (!(activeDocumentMapping?.scale_factor !== undefined && activeDocumentMapping.scale_factor !== '0')) {
        return false
    }

    // disabled all tools when ai suggestion is selected
    if (selectedAiSuggestions.length > 0) {
        return enabledToolsForAiSuggestion().includes(toolName)
    }

    // measure and pitch tools are always enabled when the scale is set
    if (enabledToolsWhenPageScaleIsSet().includes(toolName)) {
        return true
    }

    // arrow tools is available when it's joist type group and direction other
    if (toolName === Arrow.NAME) {
        return (
            (activeDrawableGroup &&
                NEW_IMUP_JOIST_TYPES_GROUP.includes(activeDrawableGroup.type) &&
                joistDirection?.toLowerCase() === JOIST_DIRECTION.OTHER) ||
            false
        )
    }

    // toggle availability when active drawable group is not set
    if (toolName === FloorLevelBreaklineTool.NAME) {
        return !isActiveDrawableGroupWithOpenings
    }

    // toggle availability of editing tools based on shapeType
    if (isEditingTool(toolName)) {
        const editingToolMappingByShape = getEditingToolTypeMapping()

        const locationFound = drawableLocations.find(
            (loc) =>
                loc.opening_group_id === activeDrawableGroup?.id &&
                loc.shapeType === editingToolMappingByShape[toolName]
        )

        return !!locationFound && isActiveDrawableGroupWithOpenings
    }

    // toggle availability of creating tools based on drawable type
    if ((activeDrawableGroup?.type || selectedMatType) && isCreationTool(toolName)) {
        const supportedDrawingTypes = determineAllSupportedDrawingTypes(
            (activeDrawableGroup?.type || selectedMatType) as DRAWABLE_TYPES
        )

        const creationToolTypeMapping = getCreationToolTypeMapping()

        return !!supportedDrawingTypes?.includes(creationToolTypeMapping[toolName])
    }

    return false
}

export const useToolEnabler = (toolName: ToolbarButtonItem['toolName']): boolean => {
    const params = useAppSelector((state) => ({
        activeDrawableGroup: selectActiveGeometryGroup(state),
        drawableLocations: selectIMUPDrawableLocations(state),
        selectedMatType: getSelectedMaterial(state),
        joistDirection: selectJoistDirection(state),
        activeDocumentMapping: selectDocumentMappingByActiveDocument(state),
        project: state.IMUP.project.project,
        overlayChunkId: selectFocusedOverlayChunkId(state),
        isOverlaidChunkLocked: selectIsOverlaidChunkLocked(state),
        selectedAiSuggestions: selectedAiSuggestionIds(state),
        areConflictsBeingReviewed: selectAreConflictsBeingReviewed(state),
        isConflictResolutionComplete: selectAreConflictsResolutionComplete(state),
        areMaterialInteractionsBlocked: selectAreMaterialInteractionsBlocked(state),
        isMaterialGroupActivationBlocked: selectIsMaterialGroupActivationBlocked(state),
    }))

    return isToolEnabled(toolName, params)
}
